import React, { forwardRef, useImperativeHandle, useRef, useState, useCallback, useMemo } from 'react';
import { Col, Drawer, Row, Spin, Tabs, Button, Form, Input, Select, message } from 'antd';
import { handlers } from '../../../helpers/helpers';
import { getCookie } from '../../../services/cookies';
import { useTranslation } from 'react-i18next';
import { useConsultationDiagnosticExam } from '../hooks/useConsultationDiagnosticExam';
import ExamDetailPage from '../../ExamPage/ExamDetailPage';
import DiagnosticSelector from '../../components/DiagnosticSelector';
import { DiagnosticCard } from '../../components/Diagnostics';
import { DiagnosticsCardsHeader } from './ConsultationDiagnostics';
import { EXAM_SPECIALTY_IDS } from '../constants/examSpecialties';

const { TabPane } = Tabs;
const { Option } = Select;


const DiagnosticExamTabContent = forwardRef(({
  record,
  onExamSaved,
  form,
  showDiagnosticTypification,
  diagnosticTypificationOptions,
  recommendatiosModal,
  open,
}, ref) => {
  const { t } = useTranslation();
  const countryCode = getCookie('country_code');

  const admissionPatientExamId = record.admission_patient_exam;

  const {
    documentUrl,
    documentLoading,
    saving,
    examRecord,
    fetchingData,
    examSaveHandlersRef,
    expertFieldValuesRef,
    selectedStateRef,
    handleSaveAllExams,
    stateField,
    otherExpertFields,
  } = useConsultationDiagnosticExam({ record, open, onExamSaved, form, showDiagnosticTypification });

  useImperativeHandle(ref, () => ({
    save: handleSaveAllExams,
    isSaving: saving,
  }));

  const isLoading = fetchingData;

  return (
    <Spin className="cmt-spin" spinning={isLoading}>
      {(record?.exam_name || examRecord?.exam_name || record?.isGroup) && (
        <>
          <div className="exam-detail-drawer__section-title">
            {`Detalle de ${record?.isGroup ? record?.name : (record?.exam_name || examRecord?.exam_name || 'examen')}`}
          </div>

          {
            (documentUrl) &&
            <>
              <p className="exam-detail-drawer__section-subtitle">
                Previsualizador de examen
              </p>
              <div className="exam-detail-drawer__image-preview">
                {documentLoading ? (
                  <Spin size="large" />
                ) : documentUrl ? (
                  <iframe
                    src={`${documentUrl}#toolbar=1`}
                    title={`Previsualizador de ${record?.isGroup ? record?.name : (record?.exam_name || examRecord?.exam_name || 'examen')}`}
                    loading="lazy"
                    allow="fullscreen"
                  />
                ) : (
                  <p className="exam-detail-drawer__empty-text">
                    No hay documento disponible
                  </p>
                )}
              </div>
            </>
          }
          <ExamDetailPage
            from={'expert'}
            fromExternal={true}
            countryCode={countryCode}
            externalExam={{
              admission_patient_id: record?.admission_patient_id,
            }}
            externalAdmissionPatientExamId={record?.isGroup ? undefined : admissionPatientExamId}
            externalGroupType={record?.isGroup ? record?.exam_type : undefined}
            onExamSaveReady={(saveExamsFn) => {
              examSaveHandlersRef.current[0] = saveExamsFn;
            }}
            saveAndSignLoading={saving}
          />

          {otherExpertFields.length > 0 && (
            <div className="exam-detail-drawer__expert-fields mt-30">
              <div className="exam-detail-drawer__section-title mb-20">
                Campos de firma especialista
              </div>
              <Row gutter={[20, 20]}>
                {otherExpertFields.map(field => (
                  <Col span={24} key={field.id}>
                    <div className="exam-detail-drawer__status-field">
                      <p className="exam-detail-drawer__status-label">{field.name}</p>
                      {field.input_type === 'select' ? (
                        <Select
                          className="cmt-select width-100"
                          defaultValue={field.result || undefined}
                          disabled={!record?.to_doctor}
                          onChange={record?.to_doctor ? (val) => {
                            expertFieldValuesRef.current[field.id] = val;
                          } : undefined}
                        >
                          {field.options?.map((option, index) => (
                            <Option key={index} value={option}>{option}</Option>
                          ))}
                        </Select>
                      ) : field.input_type === 'diagnostic' ? (
                        <div className="exam-detail-drawer__diagnostic-readonly">
                           {(Array.isArray(field.result) && field.result.length > 0)
                             ? field.result.map(diagnostic => typeof diagnostic === 'string' ? diagnostic : (diagnostic.description || diagnostic.name)).join(', ')
                             : 'Sin diagnóstico'
                           }
                        </div>
                      ) : (
                        <Input.TextArea
                          className="cmt-input"
                          defaultValue={field.result || ''}
                          disabled={!record?.to_doctor}
                          autoSize={{ minRows: 2, maxRows: 6 }}
                          onChange={record?.to_doctor ? (event) => {
                            expertFieldValuesRef.current[field.id] = event.target.value;
                          } : undefined}
                        />
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </>
      )}
      {(record?.exam_name || examRecord?.exam_name || record?.isGroup) && form
       && record?.exam_specialty_id !== EXAM_SPECIALTY_IDS[countryCode]?.ULTRASOUND && (
        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) =>
            prev.examDiagnostics !== curr.examDiagnostics ||
            prev.specialtyDiagnostics !== curr.specialtyDiagnostics
          }
        >
          {({ getFieldValue }) => {
            const examDiagnostics = getFieldValue('examDiagnostics') || [];
            const specialtyDiagnostics = getFieldValue('specialtyDiagnostics') || [];

            let diagnosticIndex = -1;
            let diagnosticType = '';
            let diagnosticData = null;

            const isSpecialty = !!record.isGroup && !!record.exam_specialty_id;

            if (isSpecialty) {
              diagnosticIndex = specialtyDiagnostics.findIndex(specialty =>
                String(specialty?.examSpecialtyId) === String(record.exam_specialty_id)
              );
              diagnosticType = 'specialtyDiagnostics';
              diagnosticData = diagnosticIndex >= 0 ? specialtyDiagnostics[diagnosticIndex] : null;
            } else {
              diagnosticIndex = examDiagnostics.findIndex(exam => String(exam.examId) === String(record.admission_patient_exam));
              diagnosticType = 'examDiagnostics';
              diagnosticData = diagnosticIndex >= 0 ? examDiagnostics[diagnosticIndex] : null;
            }

            if (diagnosticIndex < 0 || !diagnosticData) return null;

            const liveDiagnostics = getFieldValue([diagnosticType, diagnosticIndex, 'diagnostics']) || [];
            const liveExpertFieldDiagnostics = getFieldValue([diagnosticType, diagnosticIndex, 'expertFieldDiagnostics']) || [];

            return (
              <div>
                <div className="exam-detail-drawer__section-title mt-40 mb-20">
                  {record.isGroup ? `Diagnósticos de ${diagnosticData.specialtyName}` : 'Diagnósticos del examen'}
                </div>
                {record.isGroup ? (
                  <>
                    <Form.Item name={[diagnosticType, diagnosticIndex, 'examSpecialtyId']} hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item name={[diagnosticType, diagnosticIndex, 'specialtyName']} hidden>
                      <Input />
                    </Form.Item>
                  </>
                ) : (
                  <>
                    <Form.Item name={[diagnosticType, diagnosticIndex, 'examId']} hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item name={[diagnosticType, diagnosticIndex, 'examName']} hidden>
                      <Input />
                    </Form.Item>
                  </>
                )}

                <Row className="mb-20">
                  <Col lg={24}>
                    <DiagnosticSelector
                      form={form}
                      diagnosticField={[diagnosticType, diagnosticIndex, 'diagnostics']}
                      placeholder={`Buscar diagnóstico para ${record.isGroup ? diagnosticData.specialtyName : diagnosticData.examName}...`}
                      showDiagnosticTypification={showDiagnosticTypification}
                      diagnosticTypificationOptions={diagnosticTypificationOptions}
                    />
                  </Col>
                </Row>

                {liveDiagnostics.length > 0 && (
                  <>
                    <DiagnosticsCardsHeader showDiagnosticTypification={showDiagnosticTypification} />
                    <Form.List name={[diagnosticType, diagnosticIndex, 'diagnostics']}>
                      {(fields, { remove }) => (
                        <>
                          {fields.map((field, internalDiagnosticIndex) => (
                            <DiagnosticCard
                              key={field.key}
                              field={field}
                              index={internalDiagnosticIndex}
                              onRemove={remove}
                              recommendatiosModal={recommendatiosModal}
                              diagnosticField={[diagnosticType, diagnosticIndex, 'diagnostics']}
                              showDiagnosticTypification={showDiagnosticTypification}
                              diagnosticTypificationOptions={diagnosticTypificationOptions}
                              t={t}
                              form={form}
                            />
                          ))}
                        </>
                      )}
                    </Form.List>
                  </>
                )}

                {!record.isGroup && liveExpertFieldDiagnostics.length > 0 && (
                  <Form.List name={[diagnosticType, diagnosticIndex, 'expertFieldDiagnostics']}>
                    {(groups) => (
                      <>
                        {groups.map((group, groupIdx) => {
                          const groupValues = getFieldValue([diagnosticType, diagnosticIndex, 'expertFieldDiagnostics', groupIdx]) || {};
                          const fieldName = groupValues.fieldName || 'Otros campos';
                          const diagnostics = groupValues.diagnostics || [];

                          return (
                            <div key={group.key} className="mt-30">
                              <h3 className="mb-10">{fieldName}</h3>
                              {diagnostics.length > 0 && <DiagnosticsCardsHeader showDiagnosticTypification={showDiagnosticTypification} />}
                              <Form.List name={[groupIdx, 'diagnostics']}>
                                {(fields, { remove }) => (
                                  <>
                                    {fields.map((field, internalDiagnosticIndex) => (
                                      <DiagnosticCard
                                        key={field.key}
                                        field={field}
                                        index={internalDiagnosticIndex}
                                        onRemove={remove}
                                        recommendatiosModal={recommendatiosModal}
                                        diagnosticField={[diagnosticType, diagnosticIndex, 'expertFieldDiagnostics', groupIdx, 'diagnostics']}
                                        showDiagnosticTypification={showDiagnosticTypification}
                                        diagnosticTypificationOptions={diagnosticTypificationOptions}
                                        t={t}
                                        form={form}
                                      />
                                    ))}
                                  </>
                                )}
                              </Form.List>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </Form.List>
                )}
              </div>
            );
          }}
        </Form.Item>
      )}
    </Spin>
  );
});

const DiagnosticExamDrawerContent = ({
  record,
  onExamSaved,
  form,
  showDiagnosticTypification,
  diagnosticTypificationOptions,
  recommendatiosModal,
  open,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('0');
  const [globalSaving, setGlobalSaving] = useState(false);
  const tabRefs = useRef({});

  const formattedDocument = record?.document_type === 'rut'
    ? handlers.formatDocument(record?.document_number)
    : record?.document_number;

  const siblingExams = record?.siblingExams || [];
  const allExams = useMemo(() => [
    { admission_patient_exam: record.admission_patient_exam, exam_name: record.exam_name, admission_patient_id: record.admission_patient_id, to_doctor: record.to_doctor, need_expert_sign: record.need_expert_sign, allow_diagnostics: record.allow_diagnostics },
    ...siblingExams
  ], [record, siblingExams]);

  const currentExamName = useMemo(() => {
    return allExams[parseInt(activeTab)]?.exam_name || record?.exam_name;
  }, [allExams, activeTab, record?.exam_name]);

  const handleGlobalSaveAllExams = useCallback(async () => {
    setGlobalSaving(true);
    try {
      const promises = Object.values(tabRefs.current).map(tabRef => tabRef?.save?.());
      await Promise.all(promises);
      if (onExamSaved) onExamSaved();
    } catch (error) {
      console.error('Error on global saving:', error);
      message.error({
        content: 'Error al guardar cambios en los exámenes.',
        style: { marginTop: '40vh' }
      });
    } finally {
      setGlobalSaving(false);
    }
  }, [onExamSaved]);

  return (
    <>
      <h2 className="exam-detail-drawer__title">
        {`${currentExamName || 'Detalle de examen'}`}
      </h2>

      <Row gutter={[20, 16]} className="mb-20">
        <Col span={13}>
          <div className="exam-detail-drawer__patient-card">
            <p>
              Trabajador: <strong>{record?.full_name || ''}</strong>
            </p>
            <p>
              DNI: <strong>{formattedDocument || ''}</strong>
            </p>
            {record?.age && (
              <p>
                Edad: <strong>{record?.age}</strong>
              </p>
            )}
          </div>
        </Col>
      </Row>

      <Tabs className="cmt-tabs" activeKey={activeTab} onChange={setActiveTab}>
        {allExams.map((exam, index) => (
          <TabPane key={String(index)} tab={exam.exam_name}>
            <DiagnosticExamTabContent
              ref={(element) => (tabRefs.current[index] = element)}
              record={{
                ...record,
                admission_patient_exam: exam.admission_patient_exam,
                exam_name: exam.exam_name,
                admission_patient_id: exam.admission_patient_id,
                to_doctor: exam.to_doctor,
                need_expert_sign: exam.need_expert_sign,
                allow_diagnostics: exam.allow_diagnostics,
              }}
              onExamSaved={onExamSaved}
              form={form}
              showDiagnosticTypification={showDiagnosticTypification}
              diagnosticTypificationOptions={diagnosticTypificationOptions}
              recommendatiosModal={recommendatiosModal}
              open={open}
            />
          </TabPane>
        ))}
      </Tabs>

      <Row className="mt-40 mb-20" justify="end">
        <Col>
          <Button
            type="primary"
            className="cmt-button primary"
            size="large"
            onClick={handleGlobalSaveAllExams}
            loading={globalSaving}
          >
            Guardar examen
          </Button>
        </Col>
      </Row>
    </>
  );
};

const ConsultationDiagnosticExamDrawer = ({
  open,
  record,
  onClose,
  onExamSaved,
  form,
  examDiagnostics,
  specialtyDiagnostics,
  showDiagnosticTypification,
  diagnosticTypificationOptions,
  recommendatiosModal,
}) => {
  return (
    <Drawer
      className="exam-detail-drawer"
      title={null}
      placement="right"
      width="74%"
      open={open}
      onClose={onClose}
      closable={true}
      destroyOnClose
    >
      {record && (
        <DiagnosticExamDrawerContent
          record={record}
          onExamSaved={onExamSaved}
          form={form}
          examDiagnostics={examDiagnostics}
          specialtyDiagnostics={specialtyDiagnostics}
          showDiagnosticTypification={showDiagnosticTypification}
          diagnosticTypificationOptions={diagnosticTypificationOptions}
          recommendatiosModal={recommendatiosModal}
          open={open}
        />
      )}
    </Drawer>
  );
};

export default ConsultationDiagnosticExamDrawer;
